import React, { useEffect, useState } from 'react';
import {
  Scale,
  Plus,
  Pencil,
  Trash2,
  Image as ImageIcon,
  FileText,
  ListChecks,
  Calendar,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { conventionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernModal,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import { formatDateOnlyFr } from '../utils/dateFormat';

const emptyForm = {
  name: '',
  description: '',
  regles: '',
  logo: ''
};

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function formatConventionDate(value) {
  if (value == null || value === '') return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return formatDateOnlyFr(s);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

const Convention = () => {
  const { isAdmin, isManager } = useAuth();
  const canManage = isAdmin() || isManager();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await conventionsAPI.list();
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les conventions');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowFormModal(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '',
      description: c.description || '',
      regles: c.regles || '',
      logo: c.logo || ''
    });
    setShowFormModal(true);
  };

  const openView = (c) => {
    setViewItem(c);
    setShowViewModal(true);
  };

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez choisir une image');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Image trop lourde (max 2 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, logo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || '',
        regles: form.regles || '',
        logo: form.logo || null
      };
      if (editingId) {
        await conventionsAPI.update(editingId, payload);
        toast.success('Convention mise à jour');
      } else {
        await conventionsAPI.create(payload);
        toast.success('Convention créée');
      }
      setShowFormModal(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg;
      toast.error(msg || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer la convention « ${c.name} » ?`)) return;
    try {
      await conventionsAPI.remove(c.id);
      toast.success('Convention supprimée');
      load();
      if (viewItem?.id === c.id) {
        setShowViewModal(false);
        setViewItem(null);
      }
    } catch (err) {
      toast.error('Suppression impossible');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pb-10 sm:pb-12">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary-600 via-primary-700 to-slate-900 text-white shadow-xl shadow-primary-900/20 mb-8 sm:mb-10">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-72 sm:h-72 rounded-full bg-white/5 blur-3xl -mr-16 -mb-16" />
        <div className="relative px-4 py-8 sm:px-8 sm:py-10 md:py-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="flex items-start gap-4 max-w-2xl">
              <div className="flex-shrink-0 p-3 sm:p-3.5 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20">
                <Scale className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-primary-100 text-xs sm:text-sm font-medium uppercase tracking-wider mb-1">
                  Espace entreprise
                </p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                  Conventions
                </h1>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-primary-100/95 leading-relaxed max-w-xl">
                  {canManage
                    ? 'Créez et gérez les conventions internes : logo, description et règles à respecter.'
                    : 'Consultez les conventions et règles établies par la direction.'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:shrink-0">
              <div className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm text-sm">
                <BookOpen className="w-4 h-4 opacity-90" />
                <span className="font-semibold tabular-nums">{items.length}</span>
                <span className="text-primary-100 text-xs sm:text-sm">
                  {items.length <= 1 ? 'convention' : 'conventions'}
                </span>
              </div>
              {canManage && (
                <ModernButton
                  variant="secondary"
                  className="!bg-white !text-primary-700 hover:!bg-primary-50 border-0 shadow-lg w-full sm:w-auto justify-center"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={openCreate}
                >
                  Nouvelle convention
                </ModernButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      {items.length === 0 ? (
        <ModernCard className="p-8 sm:p-12 md:p-16 text-center border-2 border-dashed border-neutral-200 bg-gradient-to-b from-neutral-50/80 to-white">
          <div className="inline-flex p-4 rounded-2xl bg-primary-50/80 mb-4">
            <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-primary-500" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-neutral-900">Aucune convention pour le moment</h2>
          <p className="text-neutral-600 mt-2 text-sm sm:text-base max-w-md mx-auto">
            Les documents officiels et règles internes apparaîtront ici.
          </p>
          {canManage && (
            <ModernButton variant="primary" className="mt-6 sm:mt-8" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Créer une convention
            </ModernButton>
          )}
        </ModernCard>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 list-none p-0 m-0">
          {items.map((c) => {
            const dateLabel = formatConventionDate(c.updated_at || c.created_at);
            const hasDesc = Boolean(c.description?.trim());
            return (
              <li key={c.id} className="h-full">
                <article
                  className={[
                    'group h-full flex flex-col rounded-3xl border bg-white overflow-hidden',
                    'border-neutral-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]',
                    'ring-1 ring-black/[0.03]',
                    'hover:shadow-[0_20px_40px_-12px_rgba(15,23,42,0.12)] hover:border-primary-200/70 hover:ring-primary-500/10',
                    'transition-all duration-300 ease-out'
                  ].join(' ')}
                >
                  {/* Visuel */}
                  <div className="relative aspect-[21/9] sm:aspect-[2/1] bg-gradient-to-br from-slate-50 via-neutral-50 to-primary-50/40">
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none bg-gradient-to-t from-primary-600/[0.06] via-transparent to-transparent"
                      aria-hidden
                    />
                    <div className="absolute top-3 left-3 z-10">
                      <span className="inline-flex items-center rounded-full bg-white/90 backdrop-blur-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-700 shadow-sm ring-1 ring-black/5">
                        Convention
                      </span>
                    </div>
                    {c.logo ? (
                      <img
                        src={c.logo}
                        alt={`Logo ${c.name}`}
                        className="relative z-[1] w-full h-full object-contain object-center p-5 sm:p-6"
                      />
                    ) : (
                      <div className="relative z-[1] flex h-full flex-col items-center justify-center text-neutral-400 px-6">
                        <div className="rounded-2xl bg-white/60 p-4 ring-1 ring-neutral-200/80 shadow-sm">
                          <ImageIcon className="w-11 h-11 sm:w-12 sm:h-12 opacity-45" strokeWidth={1.25} />
                        </div>
                        <span className="text-xs mt-3 font-medium text-neutral-500">Aucun logo</span>
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="flex flex-1 flex-col px-5 pt-5 pb-4 sm:px-6 sm:pt-5 sm:pb-5">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[1.0625rem] sm:text-lg text-neutral-900 leading-snug tracking-tight line-clamp-2 group-hover:text-primary-800 transition-colors">
                        {c.name}
                      </h3>
                      {dateLabel && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100/95 px-3 py-1 text-xs font-medium text-neutral-600 ring-1 ring-neutral-200/60">
                            <Calendar className="w-3.5 h-3.5 shrink-0 text-neutral-500" aria-hidden />
                            <span className="truncate max-w-full">Mis à jour · {dateLabel}</span>
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex-1 min-h-[4.5rem] rounded-2xl border border-neutral-100 bg-gradient-to-b from-neutral-50/95 to-white px-3.5 py-3 sm:px-4 sm:py-3.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-400 mb-1.5">
                        Aperçu
                      </p>
                      <p
                        className={[
                          'text-sm leading-relaxed line-clamp-3',
                          hasDesc ? 'text-neutral-700' : 'text-neutral-400 italic'
                        ].join(' ')}
                      >
                        {hasDesc ? c.description : 'Aucune description renseignée.'}
                      </p>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 pt-4 border-t border-neutral-100/90">
                      <ModernButton
                        variant="ghost"
                        size="sm"
                        className="w-full !justify-center sm:min-h-[2.5rem] font-medium rounded-xl border border-neutral-200/80 bg-white hover:bg-neutral-50"
                        onClick={() => openView(c)}
                      >
                        Lire la convention
                      </ModernButton>
                      {canManage && (
                        <div className="space-y-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400 px-0.5">
                            Gestion
                          </p>
                          <div
                            className="flex w-full overflow-hidden rounded-xl bg-neutral-100/90 p-1 shadow-inner ring-1 ring-neutral-200/70"
                            role="group"
                            aria-label="Actions de gestion de la convention"
                          >
                            <ModernButton
                              variant="primary"
                              size="sm"
                              className="flex-1 !rounded-lg !shadow-md hover:!scale-[1.01] active:!scale-[0.99] !min-h-[2.5rem] !px-2 sm:!px-3"
                              icon={<Pencil className="w-3.5 h-3.5 shrink-0" />}
                              onClick={() => openEdit(c)}
                            >
                              Modifier
                            </ModernButton>
                            <span
                              className="w-px shrink-0 self-stretch my-1.5 bg-neutral-300/90"
                              aria-hidden
                            />
                            <ModernButton
                              variant="danger"
                              size="sm"
                              className="flex-1 !rounded-lg !shadow-md hover:!scale-[1.01] active:!scale-[0.99] !min-h-[2.5rem] !px-2 sm:!px-3"
                              icon={<Trash2 className="w-3.5 h-3.5 shrink-0" />}
                              onClick={() => handleDelete(c)}
                            >
                              Supprimer
                            </ModernButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      {/* Create / Edit */}
      <ModernModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setForm(emptyForm);
          setEditingId(null);
        }}
        title={editingId ? 'Modifier la convention' : 'Nouvelle convention'}
        size="xxl"
      >
        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
            <div className="space-y-2">
              <ModernInput
                label="Nom"
                name="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                placeholder="Ex. : Charte d'éthique interne"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">Logo (image, max 2 Mo)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFile}
                className="block w-full text-sm text-neutral-600 file:mr-3 sm:file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-primary-50 file:text-primary-700 file:font-medium hover:file:bg-primary-100 file:transition-colors"
              />
              {form.logo && (
                <div className="mt-3 flex flex-wrap items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-100">
                  <img src={form.logo} alt="Aperçu logo" className="h-16 sm:h-20 w-auto max-w-[min(100%,200px)] object-contain rounded-lg bg-white p-1 border border-neutral-100" />
                  <ModernButton type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, logo: '' }))}>
                    Retirer le logo
                  </ModernButton>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Description</label>
            <textarea
              className="w-full min-h-[100px] sm:min-h-[120px] px-3 py-2.5 border border-neutral-300 rounded-xl text-sm sm:text-base text-neutral-800 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow resize-y"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Description générale de la convention..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Règles</label>
            <textarea
              className="w-full min-h-[140px] sm:min-h-[min(40vh,240px)] px-3 py-2.5 border border-neutral-300 rounded-xl text-sm sm:text-base text-neutral-800 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow resize-y"
              value={form.regles}
              onChange={(e) => setForm((f) => ({ ...f, regles: e.target.value }))}
              placeholder="Règles détaillées, points à respecter..."
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-neutral-100 sticky bottom-0 bg-white/95 backdrop-blur-sm -mx-1 px-1 pb-1 sm:static sm:bg-transparent sm:backdrop-none">
            <ModernButton
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowFormModal(false);
                setForm(emptyForm);
                setEditingId(null);
              }}
            >
              Annuler
            </ModernButton>
            <ModernButton type="submit" variant="primary" className="w-full sm:w-auto" disabled={submitLoading}>
              {submitLoading ? 'Enregistrement...' : editingId ? 'Enregistrer' : 'Créer'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* View */}
      <ModernModal
        isOpen={showViewModal && !!viewItem}
        onClose={() => {
          setShowViewModal(false);
          setViewItem(null);
        }}
        title={viewItem?.name || 'Convention'}
        size="xxl"
      >
        {viewItem && (
          <div className="space-y-4 sm:space-y-6 max-h-[min(75vh,720px)] overflow-y-auto pr-1 -mr-1">
            {viewItem.logo && (
              <div className="flex justify-center rounded-2xl bg-gradient-to-br from-neutral-50 to-primary-50/40 p-4 sm:p-6 border border-neutral-100">
                <img
                  src={viewItem.logo}
                  alt={`Logo ${viewItem.name}`}
                  className="max-h-[min(40vh,220px)] w-auto rounded-lg object-contain shadow-sm"
                />
              </div>
            )}
            <section className="rounded-xl border border-neutral-100 bg-neutral-50/50 p-4 sm:p-5">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-primary-800 uppercase tracking-wide">
                <FileText className="w-4 h-4" />
                Description
              </h4>
              <p className="text-neutral-800 whitespace-pre-wrap mt-3 text-sm sm:text-base leading-relaxed">
                {viewItem.description?.trim() ? viewItem.description : '—'}
              </p>
            </section>
            <section className="rounded-xl border border-neutral-100 bg-white p-4 sm:p-5 shadow-sm">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-primary-800 uppercase tracking-wide">
                <ListChecks className="w-4 h-4" />
                Règles
              </h4>
              <p className="text-neutral-800 whitespace-pre-wrap mt-3 text-sm sm:text-base leading-relaxed">
                {viewItem.regles?.trim() ? viewItem.regles : '—'}
              </p>
            </section>
            {viewItem.created_by_name && (
              <p className="text-xs text-neutral-500 text-center sm:text-left pt-2 border-t border-neutral-100">
                Créé par <span className="font-medium text-neutral-700">{viewItem.created_by_name}</span>
                {formatConventionDate(viewItem.updated_at || viewItem.created_at) && (
                  <> · Mis à jour {formatConventionDate(viewItem.updated_at || viewItem.created_at)}</>
                )}
              </p>
            )}
            <div
              className={`flex flex-col gap-3 pt-4 border-t border-neutral-100 sm:flex-row sm:items-center ${
                canManage ? 'sm:justify-between' : 'sm:justify-end'
              }`}
            >
              {canManage && (
                <div
                  className="flex w-full overflow-hidden rounded-xl bg-neutral-100/90 p-1 shadow-inner ring-1 ring-neutral-200/70 sm:max-w-md sm:flex-1"
                  role="group"
                  aria-label="Actions de gestion"
                >
                  <ModernButton
                    variant="primary"
                    size="sm"
                    className="flex-1 !rounded-lg !shadow-md hover:!scale-[1.01] active:!scale-[0.99] !min-h-[2.5rem]"
                    icon={<Pencil className="w-4 h-4 shrink-0" />}
                    onClick={() => {
                      setShowViewModal(false);
                      openEdit(viewItem);
                    }}
                  >
                    Modifier
                  </ModernButton>
                  <span className="w-px shrink-0 self-stretch my-1.5 bg-neutral-300/90" aria-hidden />
                  <ModernButton
                    variant="danger"
                    size="sm"
                    className="flex-1 !rounded-lg !shadow-md hover:!scale-[1.01] active:!scale-[0.99] !min-h-[2.5rem]"
                    icon={<Trash2 className="w-4 h-4 shrink-0" />}
                    onClick={() => handleDelete(viewItem)}
                  >
                    Supprimer
                  </ModernButton>
                </div>
              )}
              <ModernButton
                variant="secondary"
                className="w-full shrink-0 sm:w-auto sm:min-w-[7.5rem]"
                onClick={() => {
                  setShowViewModal(false);
                  setViewItem(null);
                }}
              >
                Fermer
              </ModernButton>
            </div>
          </div>
        )}
      </ModernModal>
    </div>
  );
};

export default Convention;
